import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ShieldAlert } from "lucide-react";
import { askFirst, confirmWith, type LiveConfirm } from "@/lib/confirm";
import { formatDateTime, formatInt } from "@/lib/format";
import { useToast } from "@/components/Toaster";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import type { AutomationSummary } from "./AutomationsPage";

export type Draft = { version_id: number; version: number; saved_at: string; saved_by: string };
type Choice = "draft" | "current";

/** The request each choice makes: publish the draft live, or switch the published version on. */
function request(a: AutomationSummary, choice: Choice, draft?: Draft | null): { path: string; body: Record<string, unknown> } {
  return choice === "draft" && draft
    ? { path: `/api/v1/automations/${a.id}/publish`, body: { mode: "active", version_id: draft.version_id } }
    : { path: `/api/v1/automations/${a.id}/status`, body: { status: "active" } };
}

/**
 * Go live in two steps (go-live audit H-2): opening the dialog asks the server what would go live (no
 * confirm), shows that preview, and only the second button sends confirm=true with the server's
 * confirm_token. When there is an unpublished draft, Brandon picks which version goes live.
 */
export function GoLiveDialog({ a, draft, currentVersion, onClose }: { a: AutomationSummary; draft?: Draft | null; currentVersion?: number | null; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const canCurrent = a.status !== "active" && a.current_version_id != null;
  const [choice, setChoice] = useState<Choice>(draft ? "draft" : "current");
  const [preview, setPreview] = useState<{ pv: LiveConfirm; token?: string; changed?: boolean } | null>(null);

  const ask = useMutation({
    mutationFn: (c: Choice) => {
      const r = request(a, c, draft);
      return askFirst<unknown>(r.path, r.body);
    },
    onSuccess: (r) => {
      if (r.done) {
        // Nothing to confirm (it was already live): done.
        void qc.invalidateQueries({ queryKey: ["automation", a.id] });
        onClose();
        return;
      }
      setPreview({ pv: r.preview as LiveConfirm, token: r.token });
    },
  });
  useEffect(() => {
    setPreview(null);
    ask.mutate(choice);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [choice]);

  const go = useMutation({
    mutationFn: () => {
      const r = request(a, choice, draft);
      return confirmWith<unknown>(r.path, r.body, preview?.token);
    },
    onSuccess: (r) => {
      if (!r.done) {
        // Something changed since the preview (or the token ran out): show the new preview and ask again.
        setPreview({ pv: r.preview as LiveConfirm, token: r.token, changed: true });
        return;
      }
      void qc.invalidateQueries({ queryKey: ["automation", a.id] });
      void qc.invalidateQueries({ queryKey: ["automations-list"] });
      void qc.invalidateQueries({ queryKey: ["automations"] });
      toast(`${a.name} is live`);
      onClose();
    },
  });

  const pv = preview?.pv;
  const error = ask.error ?? go.error;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[85vh] overflow-y-auto">
        <DialogTitle>
          {a.status === "active" ? "Publish the draft live" : "Go live"}: {a.name}?
        </DialogTitle>
        <DialogDescription>It starts running for real: emails go out and tags change for the people in it. Read what goes live below before you confirm.</DialogDescription>

        {draft && (
          <div className="mb-3 space-y-2 rounded-md border p-3 text-sm">
            <p>
              This automation has an unpublished draft (version {draft.version}, saved {formatDateTime(draft.saved_at)} by {draft.saved_by}).
              {canCurrent ? ` Going live with the current version leaves the draft's changes out.` : ""}
            </p>
            <label className="flex items-start gap-2">
              <input type="radio" name="golive-version" className="mt-1" checked={choice === "draft"} onChange={() => setChoice("draft")} />
              <span>Publish the draft and go live (version {draft.version})</span>
            </label>
            {canCurrent && (
              <label className="flex items-start gap-2">
                <input type="radio" name="golive-version" className="mt-1" checked={choice === "current"} onChange={() => setChoice("current")} />
                <span>Go live with the current version{currentVersion ? ` (version ${currentVersion})` : ""}</span>
              </label>
            )}
          </div>
        )}

        {ask.isPending && <Skeleton className="h-24" />}
        {pv && (
          <div className="space-y-2 rounded-md bg-[var(--color-muted)] p-3 text-sm" data-testid="golive-preview">
            {preview?.changed && <p className="font-medium text-[var(--color-warning-fg)]">Something changed since you looked. Check this again, then press Go live.</p>}
            <p>{pv.action}</p>
            <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1">
              {pv.version ? (
                <>
                  <dt className="text-[var(--color-muted-foreground)]">Version</dt>
                  <dd className="num">{pv.version}</dd>
                </>
              ) : null}
              <dt className="text-[var(--color-muted-foreground)]">Emails in it</dt>
              <dd className="num">{formatInt(pv.email_steps)}</dd>
              {pv.first_email_subject && (
                <>
                  <dt className="text-[var(--color-muted-foreground)]">First email</dt>
                  <dd>“{pv.first_email_subject}”</dd>
                </>
              )}
              {(pv.triggers?.length ?? 0) > 0 && (
                <>
                  <dt className="text-[var(--color-muted-foreground)]">What starts it</dt>
                  <dd>
                    <ul className="space-y-0.5">
                      {pv.triggers!.map((t) => (
                        <li key={t} className="text-xs">
                          {t}
                        </li>
                      ))}
                    </ul>
                  </dd>
                </>
              )}
              {pv.held_real_runs ? (
                <>
                  <dt className="text-[var(--color-muted-foreground)]">People held</dt>
                  <dd>
                    <span className="num">{formatInt(pv.held_real_runs)}</span> {pv.held_real_runs === 1 ? "person was" : "people were"} held while it was paused; they move on again and can get emails right away.
                  </dd>
                </>
              ) : null}
            </dl>
            <p className="text-xs text-[var(--color-muted-foreground)]">Shadow runs still going end without sending anything (Keap finishes those people). People already in it stay on the version they started with.</p>
          </div>
        )}
        <p className="mt-3 flex items-start gap-2 rounded-md bg-[var(--color-warning-bg)] px-3 py-2 text-xs text-[var(--color-warning-fg)]">
          <ShieldAlert className="mt-0.5 size-3.5 shrink-0" /> Check the shadow comparison first. People partway through the matching Keap campaign should finish in Keap, so turn off new entrants
          there at the same time.
        </p>
        {error && <p className="mt-2 text-sm text-[var(--color-danger-fg)]">{error.message}</p>}
        <div className="mt-3 flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>
            Not now
          </Button>
          <Button variant="destructive" disabled={!pv || ask.isPending || go.isPending} onClick={() => go.mutate()}>
            {choice === "draft" && draft ? "Publish the draft and go live" : "Go live"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
